/**
 * RTWO Indexer Engine — Engine Implementation
 * Phase 12 | Module ID: RTWO-IDXR-001
 * Version: 1.0.0-phase12
 *
 * Coordinates all indexer services through dependency injection,
 * in-memory repositories, adapter registries, and the internal event bus.
 *
 * Truthful Data Policy: repositories remain empty until Protocol, Ledger,
 * and Event Bus adapters ingest real data. No fabricated indexed blocks,
 * transactions, search results, balances, account histories, or asset histories.
 */

import type { RTWOIndexerEngineStatus } from './domain-models';
import type {
  IIndexJobRegistryService,
  IBlockIndexingService,
  ITransactionIndexingService,
  IAddressIndexingService,
  IAssetIndexingService,
  IEventIndexingService,
  IIndexQueryService,
  IIndexSearchService,
  IIndexStorageBindingService,
  IIndexValidationService,
  IIndexerEventPublisherService,
  IIndexerReadinessService,
} from './service-interfaces';
import { IndexerServiceRegistry } from './registry/indexer-service-registry';
import { createIndexerEngineServices } from './services';
import type { IIndexerService } from './service-interfaces';

export class RTWOIndexerEngine {
  private static _instance: RTWOIndexerEngine | null = null;

  readonly moduleId = 'RTWO-IDXR-001';
  readonly moduleName = 'Indexer Engine';
  readonly version = '1.0.0-phase12';
  readonly phase = 'Phase 12';

  readonly jobRegistry: IIndexJobRegistryService;
  readonly blockIndexing: IBlockIndexingService;
  readonly transactionIndexing: ITransactionIndexingService;
  readonly addressIndexing: IAddressIndexingService;
  readonly assetIndexing: IAssetIndexingService;
  readonly eventIndexing: IEventIndexingService;
  readonly queryService: IIndexQueryService;
  readonly searchService: IIndexSearchService;
  readonly storageBinding: IIndexStorageBindingService;
  readonly validationService: IIndexValidationService;
  readonly eventPublisher: IIndexerEventPublisherService;
  readonly readinessService: IIndexerReadinessService;

  private _status: RTWOIndexerEngineStatus = 'not_initialized';
  private readonly _serviceRegistry = new IndexerServiceRegistry();

  private constructor() {
    const { services } = createIndexerEngineServices();

    this.jobRegistry = services.jobRegistry;
    this.blockIndexing = services.blockIndexing;
    this.transactionIndexing = services.transactionIndexing;
    this.addressIndexing = services.addressIndexing;
    this.assetIndexing = services.assetIndexing;
    this.eventIndexing = services.eventIndexing;
    this.queryService = services.queryService;
    this.searchService = services.searchService;
    this.storageBinding = services.storageBinding;
    this.validationService = services.validationService;
    this.eventPublisher = services.eventPublisher;
    this.readinessService = services.readinessService;

    const serviceList: IIndexerService[] = [
      this.jobRegistry,
      this.blockIndexing,
      this.transactionIndexing,
      this.addressIndexing,
      this.assetIndexing,
      this.eventIndexing,
      this.queryService,
      this.searchService,
      this.storageBinding,
      this.validationService,
      this.eventPublisher,
      this.readinessService,
    ];

    for (const service of serviceList) {
      this._serviceRegistry.register(service);
    }

    this._status = 'structure_ready';
  }

  static getInstance(): RTWOIndexerEngine {
    if (!RTWOIndexerEngine._instance) {
      RTWOIndexerEngine._instance = new RTWOIndexerEngine();
    }
    return RTWOIndexerEngine._instance;
  }

  async initialize(): Promise<void> {
    await Promise.all([
      this.jobRegistry.initialize(),
      this.blockIndexing.initialize(),
      this.transactionIndexing.initialize(),
      this.addressIndexing.initialize(),
      this.assetIndexing.initialize(),
      this.eventIndexing.initialize(),
      this.queryService.initialize(),
      this.searchService.initialize(),
      this.storageBinding.initialize(),
      this.validationService.initialize(),
      this.eventPublisher.initialize(),
      this.readinessService.initialize(),
    ]);
  }

  getStatus(): RTWOIndexerEngineStatus {
    return this._status;
  }

  getEngineInfo() {
    return {
      moduleId: this.moduleId,
      moduleName: this.moduleName,
      version: this.version,
      phase: this.phase,
      status: this._status,
      productionReady: false,
      rpcSourceConnected: false,
      indexStorageConfigured: false,
      indexingOperational: false,
      truthfulNote:
        'Truthful Data Policy Enforced — Indexing architecture is defined. No fake indexed data.',
    };
  }

  getAllServices() {
    return [
      this.jobRegistry.getDescriptor(),
      this.blockIndexing.getDescriptor(),
      this.transactionIndexing.getDescriptor(),
      this.addressIndexing.getDescriptor(),
      this.assetIndexing.getDescriptor(),
      this.eventIndexing.getDescriptor(),
      this.queryService.getDescriptor(),
      this.searchService.getDescriptor(),
      this.storageBinding.getDescriptor(),
      this.validationService.getDescriptor(),
      this.eventPublisher.getDescriptor(),
      this.readinessService.getDescriptor(),
    ];
  }
}

export const getIndexerEngine = (): RTWOIndexerEngine => RTWOIndexerEngine.getInstance();
